import moment from 'moment';

import { EBadge, ETable } from 'libs/ui';
import { IHotelOnlineBookingList } from 'libs/data-access/types';
import { calculateDateForFreeCancellation, calculateHotelCategory, isDateAfter, priceFormatter } from 'utils/utility';
import { GoDotFill } from 'react-icons/go';
import { Rating } from '@mantine/core';

interface ICancellationInfo {
    isRefundable: boolean;
    freeCancellationDate: string | null;
};

interface IProps {
    hotelTicket: IHotelOnlineBookingList;
};

export default function HotelVoucher({ hotelTicket }: IProps) {

    const mainGuest = hotelTicket.bookingRooms?.[0]?.guestList?.[0];
    const mainRoom = hotelTicket.bookingRooms?.[0];
    const facilities = mainRoom?.hotelBookingFacilities || [];
    const hotel = hotelTicket.paymentOrderResponse?.hotel;
    const hotelOrderDetail = hotelTicket.paymentOrderResponse?.hotelOrderDetail;
    const hotelPhone = hotel?.phones?.find(p => p.phoneType === 'PHONEHOTEL')?.phoneNumber;
    const hotelPhoneBooking = hotel?.phones?.find(p => p.phoneType === 'PHONEBOOKING')?.phoneNumber;
    const hotelFax = hotel?.phones?.find(p => p.phoneType === 'FAXNUMBER')?.phoneNumber;


    // HANDLER FUNCTIONS
    const getCancellationInfo = (policy: any): ICancellationInfo => {
        const currentDateTime = moment().format("YYYY-MM-DDTHH:mm:ssZ");
        const cancellationDate = calculateDateForFreeCancellation(policy?.from);

        return {
            isRefundable: !isDateAfter(currentDateTime, cancellationDate),
            freeCancellationDate: cancellationDate
        };
    };


    return (
        <div className="p-6 max-w-7xl mx-auto bg-white shadow-sm">
            {/* Header - just updated styling */}
            <div className="mb-8 border-b pb-4">
                <h1 className="text-2xl font-semibold text-gray-800">Voucher / Accommodation</h1>
                <p className="text-sm text-gray-600 mt-2">Booking confirmed and guaranteed - Voucher - Hotel</p>
            </div>

            {/* Reference and Hotel Info - improved styling */}
            <div className="bg-orange-50 p-5 rounded-xl border border-orange-200 shadow-sm">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-4">
                        <div className="text-lg text-gray-700">Reference number:</div>
                        <div className="text-2xl font-bold text-gray-900 mt-1">{hotelTicket.referenceNo}</div>
                        <div className="text-sm text-gray-600 mt-2">Valid for the hotel</div>
                    </div>
                    <div className="col-span-8">
                        <div className="text-lg font-semibold text-gray-800">
                            {hotel?.name}
                        </div>
                        {/* <div className="flex gap-2 items-center">
                            <p className="text-navy-blue font-medium text-sm">Rating:</p>
                            <div className="flex">
                                <Rating value={calculateHotelCategory(hotel?.rating || "0")} readOnly color="yellow" />
                            </div>
                            <p className="text-gray-500 text-sm">
                                ({hotel?.rating || "0"})
                            </p>
                        </div> */}
                        <div className="text-sm text-gray-600 mt-2">
                            Passenger name: {mainGuest?.salutation} {mainGuest?.firstName} {mainGuest?.lastName}
                            <br />
                            {/* File TO: {hotelTicket.pnr} */}
                        </div>
                    </div>
                </div>
            </div>

            {/* From-To Dates - improved styling */}
            <div className="mt-6 text-sm bg-gray-50 p-4 rounded-lg">
                <span className="font-semibold">From:</span> {hotelOrderDetail?.checkIn ? moment(hotelOrderDetail.checkIn).format('DD/MM/YYYY') : '-'}
                {" "}
                <span className="font-semibold">To:</span> {hotelOrderDetail?.checkOut ? moment(hotelOrderDetail.checkOut).format('DD/MM/YYYY') : '-'}
            </div>

            {/* Booking Details Table */}
            <div className="mt-6">
                <div className="border rounded-lg overflow-hidden">
                    <ETable>
                        <thead className="bg-gray-50">
                            <tr>
                                <th></th>
                                <th>Room type</th>
                                <th>Guest</th>
                                <th>Cancellation policy</th>
                                {/* <th>Facilities</th> */}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {hotelTicket.bookingRooms?.map((room, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{room.roomName} - {room.boardNameResponses?.[0]?.boardName}</td>
                                    <td>
                                        {room.guestList?.map((guest, index) => (
                                            <span key={index}>{guest.salutation} {guest.firstName} {guest.lastName}</span>
                                        ))}
                                    </td>
                                    <td>
                                        {room.roomRateKey.cancellationPolicies.map((policy, i) => {
                                            const { isRefundable, freeCancellationDate } = getCancellationInfo(policy);

                                            return (
                                                <div className="flex flex-col text-sm mb-2" key={i + "_cancellation_policy"}>
                                                    {!isRefundable ? (
                                                        <EBadge color="red" variant="light">Non Refundable</EBadge>
                                                    ) : (
                                                        <>
                                                            {i === 0 && (
                                                                <div className="flex items-center mb-1">
                                                                    <div className="text-green-600 text-sm">
                                                                        <span>Free cancellation until </span>
                                                                        <span className="font-semibold">
                                                                            {freeCancellationDate}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div className="flex flex-col text-red-600 text-xs">
                                                                <span>After {freeCancellationDate}:</span>
                                                                <span className="mt-1">
                                                                    {policy?.amount && <span>{priceFormatter(+policy?.amount)}</span>}
                                                                    {policy?.percent && <span>{policy?.percent}% charge</span>}
                                                                    {policy?.numberOfNights && <span>{policy?.numberOfNights} Night charge</span>}
                                                                </span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </td>
                                    {/* <td>
                                        {facilities?.slice(0, 5).map((facility, i) => (
                                            <div
                                                key={i + "_room_facility" + room.roomId}
                                                className="flex items-center gap-1 text-gray-600"
                                            >
                                                <GoDotFill size={8} className="text-blue-500" />
                                                <span className="text-xs">{facility.name}</span>
                                                {facility.feeApplied && <span className="text-xs text-gray-500">(₹)</span>}
                                            </div>
                                        ))}
                                    </td> */}
                                </tr>
                            ))}
                        </tbody>
                    </ETable>
                </div>
            </div>

            {/* Remarks - improved styling */}
            <div className="mt-8 bg-gray-50 p-5 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">Remarks</h3>
                <div className="text-sm text-gray-700">
                    {mainRoom.roomRateKey.rateComment || 'No remarks'}
                </div>
            </div>

            {/* Customer Remarks */}
            {/* <div className="mt-6">
                <h3 className="font-semibold mb-2">Customer remarks</h3>
                <div className="text-sm text-gray-700">
                    {mainRoom?.wildCards?.map((remark, index) => (
                        <span key={index}>{remark}. </span>
                    ))}
                    <p className="mt-1">The remarks for the establishment are for reference only. Hotel/beds cannot guarantee them.</p>
                </div>
            </div> */}

            {/* Hotel Information - improved styling */}
            <div className="mt-8 bg-gray-50 p-5 rounded-lg">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    {hotel?.name}
                    {mainRoom?.roomRateKey?.promotions?.map((promo, index) => (
                        <span key={index} className="text-sm text-orange-600">
                            ({promo.name})
                        </span>
                    ))}
                </h2>
                <div className="mt-3 text-sm text-gray-700">
                    <p>{hotel?.address}</p>
                    <p className="mt-1">{hotel?.postalCode} {hotel?.city}, {hotel?.place?.name}</p>
                    <div className="mt-3 flex flex-col gap-1">
                        <span>{hotelFax && `Fax: ${hotelFax} `}</span>
                        <span>{hotelPhone && `Telephone: ${hotelPhone} `}</span>
                        <span>{hotelPhoneBooking && `Telephone: ${hotelPhoneBooking} `}</span>
                        <span>{hotel?.email && `Email: ${hotel.email}`}</span>
                    </div>
                </div>
            </div>

            {/* Confirmation Info - improved styling */}
            <div className="mt-8 bg-gray-50 p-5 rounded-lg text-sm text-gray-600">
                <p>Confirmation reference from hotel: {hotelTicket.referenceNo}</p>
                <p className="mt-2">Service is confirmed but reference may be available from {moment(hotelTicket.bookingDate).add(3, 'days').format('YYYY-MM-DD')}T00:00</p>
            </div>

            {/* GPS Coordinates - Still missing from type */}
            {/* <div className="mt-4 text-sm text-gray-600">
                <p>GMS: 48° 53' 2.262" N - 2° 20' 42.320" E</p>
            </div> */}

            {/* Footer - improved styling */}
            <div className="mt-8 flex justify-between items-center text-sm text-gray-600 border-t border-gray-200 pt-4">
                <div>Booked and payable by {hotelTicket.bookingMode || 'Selected partners'}</div>
                <div>
                    Voucher generated on {moment(hotelTicket.bookingDate).format('DD/MM/YYYY')}
                </div>
            </div>
        </div>
    );
} 